import type {Pointer, Rect} from '@wix/document-services-types'
import {DomOrderStrategy, LayoutIntentType} from '../../../../constants/dsTypesConstants'
import {getMeasurementsPointer, getStructurePointer, getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import type {ConversionDal} from '../../types'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import type {LayoutIntent} from '../../conversionDal/types'
import {HEADER_NO_DUPLICATE_WIDTH_THRESHOLD} from './constants'

export const sortHeaderContainerChildren =
    (conversionDal: ConversionDal, hasDuplicatedComponent: boolean = false) =>
    (childIdA: string, childIdB: string) => {
        const getSemanticType = (id: string) =>
            conversionDal.get(getStructurePointer({id}))?.semanticType as SemanticTypes

        const getWidth = (id: string) => conversionDal.get(getMeasurementsPointer({id}))?.width ?? 0

        const isFirstRow = (id: string) => {
            const layout = conversionDal.get(getTargetLayoutPointer({id}))
            const {itemLayout} = layout ?? {}
            return itemLayout?.gridArea?.rowStart === 1
        }

        const semanticTypeA = getSemanticType(childIdA)
        const semanticTypeB = getSemanticType(childIdB)

        const isFirstRowA = isFirstRow(childIdA)
        const isFirstRowB = isFirstRow(childIdB)

        // Prioritize row 1 components over others
        if (isFirstRowA && !isFirstRowB) return -1
        if (!isFirstRowA && isFirstRowB) return 1

        // Logo is always first
        if (semanticTypeA === SemanticTypes.LOGO) return -1
        if (semanticTypeB === SemanticTypes.LOGO) return 1

        const isMenuA = semanticTypeA === SemanticTypes.MENU
        const isMenuB = semanticTypeB === SemanticTypes.MENU

        if (!hasDuplicatedComponent) {
            const widthA = getWidth(childIdA)
            const widthB = getWidth(childIdB)

            if (isMenuA && widthB > HEADER_NO_DUPLICATE_WIDTH_THRESHOLD) return -1
            if (isMenuB && widthA > HEADER_NO_DUPLICATE_WIDTH_THRESHOLD) return 1
        }
        if (isMenuA) return 1
        if (isMenuB) return -1

        // For other components, order by width (smaller width first)
        return getWidth(childIdA) - getWidth(childIdB)
    }

const compareByXThenY = (rectA: Rect, rectB: Rect) => {
    return rectA!.x - rectB!.x || rectA!.y - rectB!.y
}

const compareByXThenYRtl = (rectA: Rect, rectB: Rect) => {
    return rectB!.x - rectA!.x || rectA!.y - rectB!.y
}

const compareByYThenX = (rectA: Rect, rectB: Rect) => {
    return rectA!.y - rectB!.y || rectA!.x - rectB!.x
}

const compareByYThenXRtl = (rectA: Rect, rectB: Rect) => {
    return rectA!.y - rectB!.y || rectB!.x - rectA!.x
}

const compareByNOrder = (isRtl: boolean, rectA: Rect, rectB: Rect) => {
    return isRtl ? compareByXThenYRtl(rectA, rectB) : compareByXThenY(rectA, rectB)
}

const compareByZOrder = (isRtl: boolean, rectA: Rect, rectB: Rect) => {
    return isRtl ? compareByYThenXRtl(rectA, rectB) : compareByYThenX(rectA, rectB)
}

export type CompareFunction = (sortValueA: any, sortValueB: any) => number

export const getOrderStrategy = (layoutIntent: LayoutIntent | undefined, isRtl: boolean) => {
    const isNOrder =
        layoutIntent?.type === LayoutIntentType.SuperGrid && layoutIntent?.domOrderStrategy === DomOrderStrategy.NShape

    const orderStrategy = isNOrder ? 'N-ORDER' : 'Z-ORDER'
    const direction = isRtl ? 'RTL' : 'LTR'
    const compareFunction: CompareFunction = isNOrder
        ? (rectA, rectB) => compareByNOrder(isRtl, rectA, rectB)
        : (rectA, rectB) => compareByZOrder(isRtl, rectA, rectB)

    return {
        appliedHeuristics: [`${orderStrategy} (${direction})`],
        compareFunction
    }
}

export const generatePinnedChildLayout = () => ({
    itemLayout: {
        type: 'GridItemLayout',
        alignSelf: 'start',
        justifySelf: 'start',
        gridArea: {
            rowStart: -1,
            rowEnd: 1,
            columnStart: -1,
            columnEnd: 1
        },
        pinnedToContainer: true
    }
})

export const generateChildLayout = (startRow: number, startColumn: number, columnEnd: number | undefined) => ({
    itemLayout: {
        type: 'GridItemLayout',
        alignSelf: 'start',
        justifySelf: 'start',
        gridArea: {
            rowStart: startRow,
            rowEnd: startRow + 1,
            columnStart: startColumn,
            columnEnd: columnEnd ?? startColumn + 1
        }
    }
})

export const getContainerRowCountKey = (containerPointer: Pointer): string => {
    return `containerRowCount_${containerPointer.id}`
}

export const getContainerColumnCountKey = (containerPointer: Pointer): string => {
    return `containerColumnCount_${containerPointer.id}`
}
